<template>
  <div id="sidebar" class="relative">
    <!-- Mobile menu button -->
    <button
      v-if="!show"
      class="mobile-menu-trigger"
      aria-label="menu"
      @click="openMenu()"
    >
      <i class="mdi mdi-menu"></i>
    </button>

    <!-- Sidebar container -->
    <div class="sidebar-container" :class="{ 'sidebar-expanded': show }">
      <!-- Header section -->
      <div class="sidebar-header">
        <div class="logo-container">
          <div class="logo-icon" @click="openMenu()">
            <i class="mdi mdi-view-dashboard text-2xl text-red-600"></i>
          </div>
          <div class="logo-text">
            <span class="font-bold text-gray-800">NKR</span>
            <span class="text-sm text-gray-500 block">Admin</span>
          </div>
        </div>
        <button class="close-btn" @click="closeMenu()">
          <i class="mdi mdi-close text-gray-500 hover:text-gray-700"></i>
        </button>
      </div>

      <!-- Search box (only useful when expanded) -->
      <div class="sidebar-search" v-if="show">
        <i class="mdi mdi-magnify search-icon"></i>
        <input
          type="text"
          v-model="menuSearch"
          class="search-input"
          :placeholder="$t('message.header.search_menu') || 'Buscar...'"
        />
        <i
          v-if="menuSearch"
          class="mdi mdi-close-circle clear-icon"
          @click="menuSearch = ''"
        ></i>
      </div>

      <!-- Navigation list -->
      <nav class="navigation">
        <ul class="nav-list">
          <li
            v-for="(item, index) in searchedMenuItems"
            :key="index"
            class="nav-item"
            :class="{ 'nav-item-active': item.pageName == activeTab }"
          >
            <router-link
              :to="{ name: item.pageName, params: urlParams }"
              class="nav-link"
              :class="{ 'nav-link-active': item.pageName == activeTab }"
            >
              <div class="nav-icon">
                <!-- Icon class is defined in GeneralData.js for each menu item -->
                <i
                  :class="item.iconClass || 'mdi mdi-circle-outline'"
                  class="text-2xl"
                ></i>
              </div>
              <span class="nav-text">{{ item.displayText }}</span>
              <div
                v-if="item.pageName == activeTab"
                class="nav-indicator"
              ></div>
            </router-link>
          </li>
        </ul>
      </nav>

      <!-- Sidebar footer -->
      <div class="sidebar-footer">
        <div class="footer-content">
          <i class="mdi mdi-information-outline text-sm text-gray-400"></i>
          <span class="footer-text">v{{ appVersion }}</span>
        </div>
      </div>
    </div>

    <!-- Mobile overlay -->
    <div
      v-if="show"
      class="mobile-overlay sm:hidden"
      @click="closeMenu()"
    ></div>
  </div>
</template>

<script>
import { getMenu } from "@/api/GeneralData.js";
import { mapGetters } from "vuex";

export default {
  name: "LeftBar",
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      menuItems: [],
      menuSearch: "",
      publicPath: process.env.VUE_APP_IMG_BASE_URL
    };
  },
  async mounted() {
    this.loadMenu();
  },
  watch: {
    "$i18n.locale"() {
      this.loadMenu();
    }
  },
  computed: {
    ...mapGetters("general", ["getAdminSettings", "getActiveDepartment"]),
    appVersion() {
      return this.$store.getters["general/getAppVersion"];
    },
    filteredMenuItems() {
      return this.menuItems.filter(item => {
        if (item.stores && !item.stores.includes(this.activeStore)) {
          return false;
        }
        let enabled = true;
        if (
          item.role &&
          item.role.length > 0 &&
          !this.$store.getters["auth/hasRole"](item.role)
        ) {
          enabled = false;
        }
        if (
          !enabled &&
          item.specialPermission &&
          this.$store.getters["auth/userHasPermission"](item.specialPermission)
        ) {
          enabled = true;
        }

        // settings is configuration related to the store (settings table)
        if (!item.settings) return enabled;
        for (const settingKey of item.settings) {
          console.log("Checking setting:", settingKey);
          console.log("Setting value:", this.getAdminSettings.get(settingKey));
          const valueForKey = this.getAdminSettings.get(settingKey) || false;
          if (!valueForKey) {
            enabled = false;
            break;
          }
          if (valueForKey.value !== "true") {
            enabled = false;
            break;
          }
        }
        return enabled;
      });
    },
    searchedMenuItems() {
      if (!this.menuSearch.trim()) return this.filteredMenuItems;
      const term = this.menuSearch.trim().toLowerCase();
      return this.filteredMenuItems.filter(item =>
        (item.displayText || "").toLowerCase().includes(term)
      );
    },
    navCategories() {
      return this.$store.getters.navCategories;
    },
    urlParams() {
      const params = {
        storeCode: this.activeStore,
        departmentCode: "all"
      };

      // Only add departmentCode if it exists
      if (this.getActiveDepartment?.code) {
        params.departmentCode = this.getActiveDepartment.code;
      }

      return params;
    },
    activeStore() {
      return this.$store.getters["general/getActiveStore"]?.code;
    },
    activeTab() {
      return this.$route.name;
    }
  },
  beforeDestroy() {
    this.closeMenu();
  },
  methods: {
    loadMenu() {
      this.menuItems = getMenu(this.$i18n);
    },
    closeMenuAndGoTo(route) {
      this.closeMenu();
      this.$router.push(route);
    },
    closeMenu() {
      this.$emit("close");
    },
    openMenu() {
      this.$emit("open");
    }
  }
};
</script>
<style scoped lang="scss">
// Botón de menú mobile: va integrado sobre el header (no como tarjeta blanca)
// y por debajo del panel de chat (z-index 50) para no taparlo.
.mobile-menu-trigger {
  position: fixed;
  top: 12px;
  left: 12px;
  z-index: 45;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  background-color: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 50%;
  color: #ffffff;
  outline: none;
  cursor: pointer;
  transition: background-color 0.2s ease;
  &:hover,
  &:active {
    background-color: rgba(255, 255, 255, 0.28);
  }
  i {
    font-size: 22px;
    line-height: 1;
  }
  // el scoped gana a .sm\:hidden de Tailwind, así que el breakpoint va aquí
  @media (min-width: 640px) {
    display: none;
  }
}

// Modern sidebar styling
.sidebar-container {
  position: fixed;
  overflow-x: hidden;
  left: 0;
  top: 0;
  height: 100vh;
  background-color: white;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  border-right: 1px solid #e5e7eb;
  z-index: 40;
  transition: all 0.3s ease-in-out;
  width: 72px;
  font-size: 0.875rem;
  /* Scales sidebar to look like it was at 14px base when global is 16px */

  // Sidebar header
  .sidebar-header {
    height: 4rem;
    padding: 0 1rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid #f3f4f6;
    background: linear-gradient(to right, #fef2f2, #fef7f7);

    .logo-container {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      cursor: pointer;

      .logo-icon {
        width: 2.5rem;
        height: 2.5rem;
        border-radius: 0.5rem;
        background-color: white;
        box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
        display: flex;
        align-items: center;
        justify-content: center;
        transition: box-shadow 0.2s;

        &:hover {
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }
      }

      .logo-text {
        display: none;
        opacity: 0;
        transition: opacity 0.3s;
      }
    }

    .close-btn {
      display: none;
      padding: 0.25rem;
      border-radius: 0.375rem;
      background: transparent;
      border: none;
      cursor: pointer;
      transition: background-color 0.2s;

      &:hover {
        background-color: #f3f4f6;
      }
    }
  }

  // Search box
  .sidebar-search {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0.75rem 1rem 0;
    padding: 0.5rem 0.75rem;
    background-color: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;

    .search-icon {
      color: #9ca3af;
      font-size: 1.1rem;
      flex-shrink: 0;
    }

    .clear-icon {
      color: #9ca3af;
      font-size: 1.1rem;
      flex-shrink: 0;
      cursor: pointer;

      &:hover {
        color: #4b5563;
      }
    }

    .search-input {
      flex: 1;
      min-width: 0;
      border: none;
      background: transparent;
      outline: none;
      font-size: 0.875rem;
      color: #111827;

      &::placeholder {
        color: #9ca3af;
      }
    }
  }

  // Navigation
  .navigation {
    flex: 1;
    padding: 1rem 0;
    overflow-y: auto;

    .nav-list {
      padding: 0 0.2rem;
      list-style: none;
      margin: 0;
      overflow-x: hidden;

      .nav-item {
        position: relative;
        margin-bottom: 0.25rem;

        .nav-link {
          display: flex;
          align-items: center;
          padding: 0.75rem;
          color: #4b5563;
          border-radius: 0.5rem;
          transition: all 0.2s;
          text-decoration: none;
          position: relative;

          .nav-icon {
            width: 2rem;
            height: 2rem;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;

            i {
              font-size: 1.25rem;
              line-height: 1;
              width: 1.25rem;
              height: 1.25rem;
              display: flex;
              align-items: center;
              justify-content: center;
            }
          }

          .nav-text {
            margin-left: 0.75rem;
            font-size: 0.875rem;
            font-weight: 500;
            opacity: 0;
            visibility: hidden;
            transition: all 0.3s;
            transform: translateX(-10px);
            display: none;
            white-space: nowrap;
          }

          .nav-indicator {
            position: absolute;
            right: 0;
            top: 50%;
            width: 0.25rem;
            height: 1.5rem;
            background-color: #dc2626;
            border-radius: 0.125rem 0 0 0.125rem;
            transform: translateY(-50%);
            opacity: 0;
            transition: opacity 0.2s;
          }

          &:hover {
            background-color: #f9fafb;
            color: #111827;
            box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
            transform: translateX(2px);
          }
        }

        // Active state
        &.nav-item-active .nav-link {
          background-color: #fef2f2;
          color: #dc2626;
          box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);

          .nav-indicator {
            opacity: 1;
          }

          .nav-icon i {
            transform: scale(1.05);
          }
        }
      }
    }
  }

  // Sidebar footer
  .sidebar-footer {
    padding: 0.75rem 1rem;
    border-top: 1px solid #f3f4f6;

    .footer-content {
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 0.75rem;
      color: #9ca3af;

      i {
        font-size: 1rem;
        line-height: 1;
        width: 1rem;
        height: 1rem;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .footer-text {
        margin-left: 0.5rem;
        opacity: 0;
        transition: opacity 0.3s;
      }
    }
  }

  // Expanded state
  &.sidebar-expanded {
    width: 280px;

    .sidebar-header {
      .logo-text {
        display: block;
        opacity: 1;
      }

      .close-btn {
        display: block;
      }
    }

    .navigation .nav-list .nav-item .nav-link {
      .nav-text {
        display: block;
        opacity: 1;
        visibility: visible;
        transform: translateX(0);
      }
    }

    .sidebar-footer .footer-content .footer-text {
      opacity: 1;
    }

    // Mobile full width
    @media (max-width: 639px) {
      width: 100%;
    }
  }

  // Mobile hidden by default
  @media (max-width: 639px) {
    width: 0;
    transform: translateX(-100%);

    &.sidebar-expanded {
      transform: translateX(0);
    }
  }
}

// Mobile overlay
.mobile-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 30;
  transition: opacity 0.3s;
}

// Custom scrollbar for navigation
.navigation {
  scrollbar-width: thin;
  scrollbar-color: #cbd5e0 transparent;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #cbd5e0;
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: #a0aec0;
  }
}

// Animation improvements
.nav-link {
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to right, #dc2626, #ef4444);
    opacity: 0;
    transition: opacity 0.3s;
    z-index: -1;
  }

  &:hover::before {
    opacity: 0.05;
  }
}

// Icon animations
.nav-icon i {
  transition: transform 0.2s ease;
}

.nav-link:hover .nav-icon i {
  transform: scale(1.1);
}

// Smooth transitions for all interactive elements
* {
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}
</style>
